/**
 * A number you set.
 *
 * Rebuilt after review: the old one was a thin outlined strip with a tiny
 * minus and plus drawn as bare glyphs at its far ends and the number marooned
 * in the middle - on a wide field the controls were a third of a screen away
 * from the value they changed, and on the dark theme you could barely see them.
 *
 * Now the value and its unit sit left in a real input, with the two step
 * buttons together on the right as proper, filled buttons. They step by a
 * sensible amount for the range (1 for a party of 5, 25 for megabytes in the
 * hundreds), hold to repeat with acceleration, and Shift steps ten times as
 * far. Typing is unclamped until you leave the field, so clearing it to type
 * "12" does not snap to the minimum halfway through.
 *
 * It is a spinbutton to assistive technology, with its range and value.
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

export function NumberField({ value, min, max, onChange, suffix, disabled, className, step, label }: {
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  /** Drawn after the number - "players", "MB". */
  suffix?: string;
  disabled?: boolean;
  className?: string;
  /** How far one press moves. Defaults to something sensible for the range. */
  step?: number;
  /** The accessible name, when there is no visible label wrapping this. */
  label?: string;
}) {
  const [draft, setDraft] = useState(String(value));
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => { setDraft(String(value)); }, [value]);

  const range = max - min;
  const unit = step ?? (range > 2000 ? 50 : range > 400 ? 25 : range > 100 ? 5 : 1);
  const clamp = (n: number) => Math.min(max, Math.max(min, n));

  function commit(text: string) {
    const trimmed = text.trim();
    if (!trimmed) { setDraft(String(value)); return; }
    const n = Math.floor(Number(trimmed));
    const next = Number.isFinite(n) ? clamp(n) : value;
    setDraft(String(next));
    if (next !== value) onChange(next);
  }

  function nudge(direction: 1 | -1, big: boolean) {
    const current = valueRef.current;
    const by = unit * (big ? 10 : 1);
    /* Land on multiples of the step, so 37 + 25 is 50 rather than 62. */
    const raw = direction > 0 ? Math.floor(current / by) * by + by : Math.ceil(current / by) * by - by;
    const next = clamp(unit === 1 && !big ? current + direction : raw);
    if (next === current) return;
    valueRef.current = next;
    setDraft(String(next));
    onChange(next);
  }

  const atMin = value <= min;
  const atMax = value >= max;

  return (
    <div
      className={cn(
        'group flex h-[var(--h-md)] items-center gap-1 rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-inset pr-1 pl-3.5',
        'transition-[border-color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
        'hover:border-line focus-within:border-accent focus-within:shadow-[0_0_0_3px_var(--accent-soft)]',
        disabled && 'pointer-events-none opacity-55',
        className
      )}
    >
      <label className="flex min-w-0 flex-1 cursor-text items-baseline gap-1.5">
        <input
          type="text"
          inputMode="numeric"
          role="spinbutton"
          aria-label={label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={value + (suffix ? ' ' + suffix : '')}
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
          onBlur={(e) => commit(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); nudge(1, e.shiftKey); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); nudge(-1, e.shiftKey); }
            else if (e.key === 'PageUp') { e.preventDefault(); nudge(1, true); }
            else if (e.key === 'PageDown') { e.preventDefault(); nudge(-1, true); }
            else if (e.key === 'Home') { e.preventDefault(); onChange(min); }
            else if (e.key === 'End') { e.preventDefault(); onChange(max); }
          }}
          style={{ width: Math.max(2, draft.length + 0.5) + 'ch' }}
          className="min-w-0 bg-transparent text-[16px] font-bold text-ink tabular-nums outline-none"
        />
        {suffix ? <span className="truncate text-[14px] font-medium text-mute">{suffix}</span> : null}
        {value === 0 && min === 0 ? <span className="truncate text-[13px] text-ghost">· no limit</span> : null}
      </label>

      <StepButton direction={-1} disabled={disabled || atMin} onStep={nudge} label={'Decrease by ' + unit} />
      <StepButton direction={1} disabled={disabled || atMax} onStep={nudge} label={'Increase by ' + unit} />
    </div>
  );
}

/**
 * Press to step once; hold to keep stepping, faster the longer you hold.
 * Shift while pressing steps ten at a time.
 */
function StepButton({ direction, disabled, onStep, label }: {
  direction: 1 | -1;
  disabled?: boolean;
  onStep: (direction: 1 | -1, big: boolean) => void;
  label: string;
}) {
  const timer = useRef<number | null>(null);

  const stop = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => stop, []);

  function start(e: React.PointerEvent) {
    if (disabled || e.button !== 0) return;
    e.preventDefault();
    const big = e.shiftKey;
    onStep(direction, big);
    let delay = 380;
    const tick = () => {
      onStep(direction, big);
      delay = Math.max(45, delay * 0.8);
      timer.current = window.setTimeout(tick, delay);
    };
    timer.current = window.setTimeout(tick, delay);
  }

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStep(direction, e.shiftKey); } }}
      className={cn(
        'grid h-[calc(var(--h-md)-10px)] w-[calc(var(--h-md)-8px)] flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft bg-surface-2 text-ink outline-none',
        'transition-[background-color,border-color,transform,opacity] duration-[var(--t-press,90ms)] ease-[var(--ease)]',
        'hover:border-line hover:bg-surface-3 active:scale-[.94] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        'disabled:cursor-not-allowed disabled:opacity-35 disabled:active:scale-100'
      )}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
        {direction > 0 ? <path d="M12 5v14M5 12h14" /> : <path d="M5 12h14" />}
      </svg>
    </button>
  );
}
